import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify-email/${token}`);
      setStatus('success');
      setMessage(response.data.message);
      
      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        navigate('/?showLogin=true');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Error al verificar el email. El enlace puede haber expirado.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-100 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {/* Logo */}
        <img 
          src="/viubux_logo_transparent.png" 
          alt="ViubuxConnect" 
          className="h-16 mx-auto mb-6"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />

        {status === 'verifying' && (
          <>
            <div className="mb-6">
              <Loader2 className="h-16 w-16 mx-auto text-purple-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Verificando tu email...
            </h1>
            <p className="text-gray-600">
              Por favor espera mientras verificamos tu dirección de correo electrónico.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6">
              <div className="h-20 w-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              ¡Email Verificado!
            </h1>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Serás redirigido automáticamente en 3 segundos...
            </p>
            <Link
              to="/?showLogin=true"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Iniciar Sesión
              <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6">
              <div className="h-20 w-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Error de Verificación
            </h1>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <div className="space-y-3">
              <Link
                to="/"
                className="block w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Volver al Inicio
              </Link>
              <ResendVerificationButton />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResendVerificationButton() {
  const [email, setEmail] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="p-4 bg-green-50 rounded-lg text-green-700 text-sm">
        <Mail className="h-5 w-5 inline mr-2" />
        Si el email existe, recibirás un nuevo enlace de verificación.
      </div>
    );
  }

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="text-purple-600 hover:text-purple-800 text-sm underline"
      >
        ¿Necesitas un nuevo enlace de verificación?
      </button>
    );
  }

  return (
    <form onSubmit={handleResend} className="mt-4 space-y-3">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Ingresa tu email"
        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        required
      />
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={loading}
        className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            <Mail className="h-4 w-4" />
            Reenviar enlace
          </>
        )}
      </button>
    </form>
  );
}
